import { apiClient } from './apiClient';
import type { Timesheet, TimesheetEntry, Project, CostCentre, Employee } from '@/types/timesheet';

function parseTimesheetDates(timesheet: any): Timesheet {
  return {
    ...timesheet,
    weekEnding: timesheet.weekEnding ? new Date(timesheet.weekEnding) : timesheet.weekEnding,
    submittedAt: timesheet.submittedAt ? new Date(timesheet.submittedAt) : timesheet.submittedAt,
    approvedAt: timesheet.approvedAt ? new Date(timesheet.approvedAt) : timesheet.approvedAt,
    rejectedAt: timesheet.rejectedAt ? new Date(timesheet.rejectedAt) : timesheet.rejectedAt,
    recalledAt: timesheet.recalledAt ? new Date(timesheet.recalledAt) : timesheet.recalledAt,
    // Workflow fields (workflowInstanceId, currentStageName, currentStageOrder, totalStages) 
    // are strings/numbers and are preserved by the spread operator
  };
}

function parseProjectDates(project: any): Project {
  return {
    ...project,
    lastUsed: project.lastUsed ? new Date(project.lastUsed) : project.lastUsed,
  };
}

function parseCostCentreDates(costCentre: any): CostCentre {
  return {
    ...costCentre,
    lastUsed: costCentre.lastUsed ? new Date(costCentre.lastUsed) : costCentre.lastUsed,
  };
}

export const timesheetApi = {
  async getTimesheetById(id: string): Promise<Timesheet> {
    const timesheet = await apiClient.get<any>(`/timesheets/${id}`);
    return parseTimesheetDates(timesheet);
  },

  async getTimesheetsByEmployeeId(employeeId: string): Promise<Timesheet[]> {
    const timesheets = await apiClient.get<any[]>(`/timesheets/employee/${employeeId}`);
    return timesheets.map(parseTimesheetDates);
  },

  async createTimesheet(timesheet: Timesheet): Promise<Timesheet> {
    const created = await apiClient.post<any, Timesheet>('/timesheets', timesheet);
    return parseTimesheetDates(created);
  },

  async updateTimesheet(id: string, timesheet: Timesheet): Promise<void> {
    return apiClient.put<void, Timesheet>(`/timesheets/${id}`, timesheet);
  },

  async deleteTimesheet(id: string): Promise<void> {
    return apiClient.delete<void>(`/timesheets/${id}`);
  },

  async addTimesheetEntry(timesheetId: string, entry: TimesheetEntry): Promise<TimesheetEntry> {
    return apiClient.post<TimesheetEntry, TimesheetEntry>(`/timesheets/${timesheetId}/entries`, entry);
  },

  async getAllProjects(): Promise<Project[]> {
    const projects = await apiClient.get<any[]>('/projects');
    return projects.map(parseProjectDates);
  },

  async getAllCostCentres(): Promise<CostCentre[]> {
    const costCentres = await apiClient.get<any[]>('/costcentres');
    return costCentres.map(parseCostCentreDates);
  },

  async getEmployeeById(id: string): Promise<Employee> {
    return apiClient.get<Employee>(`/employees/${id}`);
  },

  async getAllEmployees(): Promise<Employee[]> {
    return apiClient.get<Employee[]>('/employees');
  },
};
